import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './GuardianPayment.css';

/**
 * PaymentSuccess - Displays payment success confirmation
 * This is the page users are redirected to after successful GoCardless/Nuapay payment
 * Matches Laravel's guardian_pay.payment_successful view
 */
const PaymentSuccess = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (paymentId) {
      loadPaymentData();
    }
  }, [paymentId]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);

      // Fetch payment status from backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guardian-pay/status/${paymentId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setPaymentData(data.data);
      } else {
        setError(data.message || 'Failed to load payment information');
      }
    } catch (error) {
      console.error('Error loading payment data:', error);
      // Don't show error toast, just set minimal success state
      setPaymentData({
        status: 'completed',
        reference: paymentId,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="guardian-payment">
        <div className="loading-container" style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner-border text-success" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px' }}>Loading payment confirmation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="guardian-payment">
      <section style={{ marginTop: '40px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div className="panel-body text-center" style={{ padding: '40px' }}>
              {/* Success Icon */}
              <div style={{ marginBottom: '30px' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#28a745',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>

              <h2 style={{ color: '#28a745', marginBottom: '20px' }}>
                Payment Successful!
              </h2>

              <p style={{ fontSize: '16px', color: '#555', marginBottom: '30px' }}>
                Thank you for your payment. Your transaction has been processed successfully.
              </p>

              {paymentData && (
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '30px',
                    textAlign: 'left',
                  }}
                >
                  <h5 style={{ marginBottom: '15px', color: '#12274e' }}>Payment Details</h5>

                  {paymentData.amount && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span>Amount:</span>
                      <strong>&pound;{parseFloat(paymentData.amount).toFixed(2)}</strong>
                    </div>
                  )}

                  {paymentData.reference && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span>Reference:</span>
                      <strong style={{ fontSize: '12px' }}>{paymentData.reference}</strong>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span>Status:</span>
                    <span
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}
                    >
                      {paymentData.status === 'completed' ? 'Completed' : paymentData.status}
                    </span>
                  </div>

                  {paymentData.provider && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Provider:</span>
                      <strong style={{ textTransform: 'capitalize' }}>{paymentData.provider}</strong>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  A confirmation email has been sent to your registered email address.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    // Clear all local storage before redirecting to login
                    localStorage.clear();
                    sessionStorage.clear();
                    navigate('/login');
                  }}
                  className="btn btn-success btn-lg"
                >
                  Continue to Login
                </button>
              </div>

              <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <p style={{ color: '#999', fontSize: '12px', marginBottom: '5px' }}>
                  Need help? Contact us at:
                </p>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  <strong>guardiancare@global-guardians.co.uk</strong>
                  <br />
                  +44 (0) 333 200 1010
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccess;
